import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, Inbox, Users, Activity, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Build a human-readable description from action + entity + details
// Backend enriches details with entity names resolved from entity_id for all log types
const getActivityDescription = (log) => {
  const d = log.details || {};
  const action = log.action;
  const entity = log.entity;

  switch (action) {
    case 'LOGIN':
      return 'Logged in';
    case 'SWITCH_ACCOUNT': {
      const to = d.to_client_name || d.to_client || '?';
      return `Switched account to "${to}"`;
    }
    case 'CREATE':
      if (entity === 'Template')     return d.name        ? `Created template: ${d.name}`           : 'Created a template';
      if (entity === 'TeamMember')   return d.member_name ? `Created team member: ${d.member_name}` : 'Created a team member';
      if (entity === 'Client')       return d.client_name ? `Created client: ${d.client_name}`       : 'Created a client';
      if (entity === 'Conversation') return d.to          ? `Started conversation with ${d.to}`      : 'Started a conversation';
      return `Created ${entity}`;
    case 'UPDATE':
      if (entity === 'User' && d.action === 'password_change')       return 'Changed own password';
      if (entity === 'User' && d.action === 'admin_password_reset')  return 'Reset user password (admin)';
      if (entity === 'Client' && d.action === 'regenerate_webhook_token') return 'Regenerated webhook token';
      if (entity === 'TeamMember') {
        const name = d.member_name ? `: ${d.member_name}` : '';
        return `Updated team member${name}`;
      }
      if (entity === 'Client') {
        const changes = d.changes || {};
        if (changes.name) return `Renamed client to: ${changes.name}`;
        return 'Updated client settings';
      }
      return `Updated ${entity}`;
    case 'EDIT':
      return d.name ? `Edited template: ${d.name}` : 'Edited template';
    case 'DELETE':
      if (entity === 'Template')   return d.name        ? `Deleted template: ${d.name}`           : 'Deleted a template';
      if (entity === 'TeamMember') return d.member_name ? `Deleted team member: ${d.member_name}` : 'Deleted a team member';
      if (entity === 'Client')     return d.client_name ? `Deleted client: ${d.client_name}`       : 'Deleted a client';
      return `Deleted ${entity}`;
    case 'SUBMIT':
      return d.name ? `Submitted template for approval: ${d.name}` : 'Submitted template for WhatsApp approval';
    case 'SYNC':
      return d.total_from_wa !== undefined
        ? `Synced templates — ${d.total_from_wa} total (${d.added || 0} new, ${d.updated || 0} updated)`
        : 'Synced templates';
    case 'SEND_MESSAGE':
      return d.to ? `Sent message to ${d.to}` : 'Sent a message';
    case 'CREATE_CONVERSATION':
      if (d.to && d.template) return `Started conversation with ${d.to} using: ${d.template}`;
      if (d.to) return `Started conversation with ${d.to}`;
      return 'Started a conversation';
    default: return `${action} — ${entity}`;
  }
};

const DashboardPage = () => {
  const { client } = useAuth();
  const [stats, setStats] = useState({
    templates: { total: 0, approved: 0, pending: 0 },
    conversations: { total: 0, unread: 0 },
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [templatesRes, conversationsRes, activityRes] = await Promise.all([
        axios.get(`${API_URL}/templates`),
        axios.get(`${API_URL}/conversations`),
        axios.get(`${API_URL}/activity-logs?limit=5`)
      ]);

      const templates = templatesRes.data;
      // conversations endpoint now returns {items: [], has_more: bool}
      const conversationsData = conversationsRes.data;
      const conversations = conversationsData.items || conversationsData || [];

      setStats({
        templates: {
          total: templates.length,
          approved: templates.filter(t => t.status === 'APPROVED').length,
          pending: templates.filter(t => t.status === 'PENDING').length
        },
        conversations: {
          total: conversations.length,
          unread: conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0)
        },
        recentActivity: activityRes.data.logs || []
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Templates',
      value: stats.templates.total,
      subtitle: `${stats.templates.approved} approved`,
      icon: FileText,
      color: '#131517'
    },
    {
      title: 'Conversations',
      value: stats.conversations.total,
      subtitle: `${stats.conversations.unread} unread`,
      icon: Inbox,
      color: '#3BAB59'
    },
    {
      title: 'Pending Templates',
      value: stats.templates.pending,
      subtitle: 'Awaiting approval',
      icon: Clock,
      color: '#F9BC34'
    }
  ];

  const getActivityIcon = (action) => {
    switch (action) {
      case 'CREATE': return <CheckCircle className="w-4 h-4 text-[#3BAB59]" />;
      case 'DELETE': return <XCircle className="w-4 h-4 text-[#EB493D]" />;
      case 'UPDATE': return <AlertCircle className="w-4 h-4 text-[#F9BC34]" />;
      default: return <Activity className="w-4 h-4 text-[#595C5C]" />;
    }
  };

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-[30px] font-semibold text-[#131517]">Dashboard</h1>
        <p className="text-[14px] text-[#595C5C] mt-1">Welcome back to {client?.name || 'your workspace'}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="border-[#D9E2E5]">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[12px] font-medium text-[#8A8A8B] uppercase tracking-wider">{stat.title}</p>
                  <p className="text-[32px] font-semibold text-[#131517] mt-1">{stat.value}</p>
                  <p className="text-[13px] text-[#595C5C] mt-1">{stat.subtitle}</p>
                </div>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat.color}10` }}>
                  <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Webhook Info */}
      {client && (
        <Card className="border-[#D9E2E5]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[18px] font-semibold text-[#131517]">Webhook Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Meta Webhook URL — primary */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[12px] font-medium text-[#8A8A8B] uppercase tracking-wider">Meta Webhook URL</p>
                <span className="text-[10px] bg-[#3BAB59]/10 text-[#3BAB59] border border-[#3BAB59]/30 px-1.5 py-0.5 rounded font-medium">Register this in Meta App Dashboard</span>
              </div>
              <code className="block bg-[#EFEFEF] px-3 py-2 rounded-lg text-[13px] text-[#131517] break-all">
                {process.env.REACT_APP_BACKEND_URL}/api/meta-webhook/{client.id}
              </code>
              <p className="text-[11px] text-[#8A8A8B] mt-1">
                Go to <strong>Meta App Dashboard → WhatsApp → Configuration → Webhook</strong> and enter this as the Callback URL.
              </p>
            </div>

            {/* Verify Token */}
            <div>
              <p className="text-[12px] font-medium text-[#8A8A8B] uppercase tracking-wider mb-1">Verify Token <span className="text-[10px] font-normal normal-case">(use this as Verify Token in Meta Dashboard)</span></p>
              <code className="block bg-[#EFEFEF] px-3 py-2 rounded-lg text-[13px] text-[#131517] break-all">
                {client.webhook_token}
              </code>
            </div>

            <div className="border-t border-[#D9E2E5] pt-3">
              <p className="text-[12px] font-medium text-[#8A8A8B] uppercase tracking-wider mb-1">Secured Webhook URL <span className="text-[10px] font-normal normal-case">(for third-party middleware — requires Bearer token)</span></p>
              <code className="block bg-[#EFEFEF] px-3 py-2 rounded-lg text-[13px] text-[#131517] break-all">
                {process.env.REACT_APP_BACKEND_URL}/api/webhook/{client.id}
              </code>
            </div>

          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <Card className="border-[#D9E2E5]">
        <CardHeader className="pb-3">
          <CardTitle className="text-[18px] font-semibold text-[#131517]">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.recentActivity.length > 0 ? (
            <div className="space-y-3">
              {stats.recentActivity.map((log, index) => (
                <div key={log.id || index} className="flex items-center gap-3 py-2 border-b border-[#D9E2E5] last:border-0">
                  {getActivityIcon(log.action)}
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] text-[#131517] font-medium truncate">
                      {getActivityDescription(log)}
                    </p>
                    <p className="text-[11px] text-[#8A8A8B]">
                      {log.user?.name || log.user?.email || 'Unknown'} • {new Date(log.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[14px] text-[#8A8A8B] text-center py-8">No recent activity</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
